import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';
import InstallmentModal from './InstallmentModal';
import SaleBikeModal from './SaleBikeModal';
import EditSaleModal from './EditSaleModal'; 
import BikeLifeCycleView from './BikeLifeCycleView';

const SalesInventory = () => {
    // Local date properly generate karne ke liye helper function ('YYYY-MM-DD')
    const getLocalDateStr = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const todayStr = getLocalDateStr();

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    
    // ⚡ Default by-default aaj ki date set kar di hai taake page load par sirf aaj ki sales show hon
    const [startDate, setStartDate] = useState(todayStr);
    const [endDate, setEndDate] = useState(todayStr);

    // Modals states
    const [selectedSale, setSelectedSale] = useState(null);
    const [showSaleModal, setShowSaleModal] = useState(false);
    const [selectedEditSale, setSelectedEditSale] = useState(null);
    const [viewingBikeId, setViewingBikeId] = useState(null);

    const [canDelete, setCanDelete] = useState(false); 
    const [canEdit, setCanEdit] = useState(false);
    const limit = 10;

    // Permissions check
    const checkUserPermissions = async () => {
        try {
            const res = await API.get('/v1/sales/check-permissions');
            if (res.data.success) {
                setCanDelete(res.data.canDelete);
                setCanEdit(res.data.canEdit);
            }
        } catch (err) {
            console.error("Permission Check Error:", err);
        }
    };

    // 100% Exact Date Format
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return String(dateStr).split('T')[0];
    };

    const fetchSales = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get(`/v1/sales`, {
                params: { 
                    page: currentPage, 
                    limit: limit, 
                    search: searchTerm.trim(),
                    start_date: startDate,
                    end_date: endDate
                }
            });
            
            if (res.data.success) {
                setSales(res.data.data || []);
                setTotalRecords(res.data.total || res.data.totalPages * limit || 0);
            }
        } catch (err) {
            console.error("Fetch Sales Error:", err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, startDate, endDate]);

    useEffect(() => {
        checkUserPermissions(); 
    }, []);

    useEffect(() => {
        const delaySearch = setTimeout(() => {
            fetchSales();
        }, 300);
        return () => clearTimeout(delaySearch);
    }, [fetchSales]);

    const handleResetToToday = () => {
        setStartDate(todayStr);
        setEndDate(todayStr);
        setSearchTerm('');
        setCurrentPage(1);
    };

    const handleDeleteSale = async (id) => {
        if (window.confirm("Are you sure you want to delete this sale record? All related transactions will be reversed.")) {
            try {
                const res = await API.delete(`/v1/sales/${id}`);
                if (res.data.success) {
                    alert("Record Deleted Successfully");
                    fetchSales();
                }
            } catch (err) {
                alert("Delete failed: " + (err.response?.data?.error || "Server Error"));
            }
        }
    };

    const totalPages = Math.ceil(totalRecords / limit) || 1;

    return (
        <div>
            {/* Filter Card */}
            <div style={styles.filterCard}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '15px', marginBottom: '15px', alignItems: 'flex-end' }}>
                    <div>
                        <label style={styles.label}>Start Date</label>
                        <input 
                            type="date" 
                            style={styles.input} 
                            value={startDate} 
                            onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>End Date</label>
                        <input 
                            type="date" 
                            style={styles.input} 
                            value={endDate} 
                            onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>Quick Search (Name, CNIC, Engine, Chassis)</label>
                        <input 
                            placeholder="Search Name, CNIC, Engine, Chassis..."
                            style={styles.input}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                    <div>
                        <button onClick={() => setShowSaleModal(true)} style={styles.mainBtn}>+ New Sale</button>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 'bold' }}>
                        Total Sold Units: <span style={{ color: '#0f172a' }}>{totalRecords}</span>
                    </span>
                    <button onClick={handleResetToToday} style={styles.resetBtn}>
                        Reset to Today
                    </button>
                </div>
            </div>

            {/* Table Card */}
            <div style={styles.tableCard}>
                <div style={styles.scrollWrapper}>
                    <table style={styles.table}>
                        <thead style={styles.thead}>
                            <tr>
                                <th style={styles.th}>Sale Date</th>
                                <th style={styles.th}>Customer Details</th>
                                <th style={styles.th}>Vehicle Info</th>
                                <th style={styles.th}>Sale Mode</th>
                                <th style={styles.th}>Net Amount</th>
                                <th style={styles.th}>Remaining</th>
                                <th style={{...styles.th, textAlign: 'center'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={styles.loader}>Loading Sales Data...</td></tr>
                            ) : sales.length > 0 ? (
                                sales.map((s) => (
                                    <tr key={s.id} style={styles.tr}>
                                        <td style={styles.td}>
                                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                                                {formatDate(s.sale_date)}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.pText}>{s.customer_name}</div>
                                            <div style={styles.sText}>CNIC: {s.cnic}</div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.pText}>{s.model_name}</div>
                                            <div style={styles.sText}>Eng: {s.engine_number}</div>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={s.sale_type?.toLowerCase() === 'installment' ? styles.bBlue : styles.bGrey}>
                                                {s.sale_type}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, fontWeight: '700', color: '#059669' }}>
                                            Rs. {parseInt(s.net_amount || 0).toLocaleString()}
                                        </td>
                                        <td style={styles.td}>
                                            {parseInt(s.remaining_balance || 0) > 0 ? (
                                                <span style={styles.due}>Rs. {parseInt(s.remaining_balance).toLocaleString()}</span>
                                            ) : (
                                                <span style={styles.paid}>Paid</span>
                                            )}
                                        </td>
                                        <td style={{...styles.td, textAlign: 'center'}}>
                                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', alignItems: 'center' }}>
                                                {s.sale_type?.toLowerCase() === 'installment' && (
                                                    <button onClick={() => setSelectedSale(s)} style={styles.actBtn}>Ledger</button>
                                                )}
                                                {canEdit && (
                                                    <button onClick={() => setSelectedEditSale(s)} style={styles.editBtn}>Edit</button>
                                                )}
                                                {canDelete && (
                                                    <button onClick={() => handleDeleteSale(s.id)} style={styles.delBtn}>Delete</button>
                                                )}
                                                <button 
                                                    onClick={() => setViewingBikeId(s.bike_master_id)} 
                                                    style={styles.viewBtn}
                                                >
                                                    Profile
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontWeight: 'bold' }}>
                                        No sales records found for today.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={styles.paginationRow}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={styles.pageBtn}>Prev</button>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Page {currentPage} of {totalPages}</span>
                    <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} style={styles.pageBtn}>Next</button>
                </div>
            </div>

            {/* Modals */}
            {showSaleModal && <SaleBikeModal onClose={() => setShowSaleModal(false)} onRefresh={fetchSales} />}
            {selectedSale && <InstallmentModal sale={selectedSale} onClose={() => { setSelectedSale(null); fetchSales(); }} />}
            {selectedEditSale && (
                <EditSaleModal 
                    sale={selectedEditSale} 
                    onClose={() => setSelectedEditSale(null)} 
                    onRefresh={fetchSales} 
                />
            )}
            {viewingBikeId && (
                <BikeLifeCycleView 
                    bikeId={viewingBikeId} 
                    onClose={() => setViewingBikeId(null)} 
                />
            )}
        </div>
    );
};

const styles = {
    filterCard: { background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' },
    label: { display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#475569', textTransform: 'uppercase', marginBottom: '5px' },
    input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', boxSizing: 'border-box', background: '#fff' },
    resetBtn: { padding: '6px 12px', background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
    mainBtn: { background: '#2563eb', color: '#fff', padding: '10px 18px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '13px', width: '100%', whiteSpace: 'nowrap' },
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', width: '100%', overflow: 'hidden' },
    tableCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    scrollWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
    thead: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    th: { padding: '15px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: '0.2s' },
    td: { padding: '15px', fontSize: '13px', verticalAlign: 'middle' },
    pText: { fontWeight: '600', color: '#334155', fontSize: '13px', margin: 0 },
    sText: { fontSize: '11px', color: '#94a3b8', marginTop: '2px' },
    due: { color: '#e11d48', fontWeight: '700', background: '#fff1f2', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' },
    paid: { color: '#059669', fontWeight: '700', background: '#ecfdf5', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' },
    bBlue: { background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' },
    bGrey: { background: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' },
    actBtn: { background: '#1e293b', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '11px' },
    editBtn: { background: '#0284c7', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '11px' },
    delBtn: { background: '#be123c', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '11px' },
    viewBtn: { padding: '6px 10px', background: '#475569', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' },
    paginationRow: { padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderTop: '1px solid #e2e8f0' },
    pageBtn: { padding: '6px 14px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
    loader: { textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: 'bold' }
};

export default SalesInventory;